import { useEffect, useState } from "react";
import { Router, Route, Switch, Redirect, useRoute, useLocation } from "wouter";
import { useAppStore } from "./store/app";
import LoginScreen from "./components/auth/LoginScreen";
import Header from "./components/ui/Header";
import Sidebar from "./components/ui/Sidebar";
import ErrorBoundary from "./components/ui/ErrorBoundary";
import Dashboard from "./components/dashboard/Dashboard";
import VacancyLibrary from "./components/vacancies/VacancyLibrary";
import ConsultantProfile from "./components/profile/ConsultantProfile";
import VacancyEvaluator from "./components/vacancies/VacancyEvaluator";
import SettingsScreen from "./components/settings/SettingsScreen";
import WorkflowWorkspace from "./components/workflows/WorkflowWorkspace";
import ConsolidationScreen from "./components/consolidation/ConsolidationScreen";
import AdminDashboard from "./components/admin/AdminDashboard";
import ConsultantsScreen from "./components/consultants/ConsultantsScreen";
import ConsultantDossier from "./components/consultants/ConsultantDossier";
import StackScreen from "./components/stack/StackScreen";
import CustomersScreen from "./components/customers/CustomersScreen";
import DocsScreen from "./components/docs/DocsScreen";
import ModuleDocButton from "./components/docs/ModuleDocButton";
import ModuleDocPane from "./components/docs/ModuleDocPane";
import { isDocsPaneOpen, subscribeDocsPane } from "./lib/docs/pane-state";
import { silentSignIn } from "./lib/auth/msal";
import { completeSignIn } from "./lib/auth/session";
import { loadPrefs } from "./lib/fs/prefs";
import { checkMasterCvExists } from "./lib/api/profile";
import { readSettings, resolveDefaultLlmId } from "./lib/fs/config";

// URL-based navigation via wouter. Each screen has a real route; the browser
// back button, deep-linking, and multi-instance-via-browser-tab all work.
// See docs/setup/dev-onboarding.md for the route map.

function ConsultantDossierRoute() {
  const [, params] = useRoute<{ oid: string }>("/consultants/:oid");
  const [, navigate] = useLocation();
  const setActive = useAppStore((s) => s.setActiveCandidateFolder);
  const oid = params?.oid ?? "";

  // Sync activeCandidateFolder (still the store field name) so screens that
  // navigate here can pick up the context. The value is now an OID; store
  // rename is a follow-up cleanup.
  useEffect(() => {
    if (oid) setActive(oid);
  }, [oid, setActive]);

  if (!oid) return null;
  return <ConsultantDossier folder={oid} onBack={() => navigate("/consultants")} />;
}

function WorkflowRoute() {
  const [, params] = useRoute<{ id: string }>("/workflows/:id");
  const setActiveWorkflowId = useAppStore((s) => s.setActiveWorkflowId);
  const id = params?.id ?? null;

  useEffect(() => {
    setActiveWorkflowId(id);
    return () => setActiveWorkflowId(null);
  }, [id, setActiveWorkflowId]);

  if (!id) return null;
  return <WorkflowWorkspace />;
}

function SignedInShell() {
  const [paneOpen, setPaneOpen] = useState(isDocsPaneOpen);
  useEffect(() => subscribeDocsPane(() => setPaneOpen(isDocsPaneOpen())), []);

  return (
    <div className="flex h-screen flex-col bg-canvas dark:bg-[#131825] overflow-hidden">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-y-auto min-w-0">
          <AppRoutes />
        </main>
        {paneOpen && <ModuleDocPane />}
      </div>
      <ModuleDocButton />
    </div>
  );
}

function AppRoutes() {
  const [pathname] = useLocation();
  return (
    <ErrorBoundary key={pathname}>
      <Switch>
        <Route path="/" component={Dashboard} />
        <Route path="/dashboard"><Redirect to="/" /></Route>

        <Route path="/consultants" component={ConsultantsScreen} />
        <Route path="/consultants/:oid" component={ConsultantDossierRoute} />
        <Route path="/customers" component={CustomersScreen} />
        <Route path="/matrix" component={AdminDashboard} />

        <Route path="/vacancies" component={VacancyLibrary} />
        <Route path="/vacancies/new" component={VacancyEvaluator} />
        <Route path="/evaluator"><Redirect to="/vacancies/new" /></Route>

        <Route path="/workflows/:id" component={WorkflowRoute} />

        <Route path="/consolidation" component={ConsolidationScreen} />
        <Route path="/stack" component={StackScreen} />
        <Route path="/settings" component={SettingsScreen} />
        <Route path="/profile" component={ConsultantProfile} />

        <Route path="/docs" component={DocsScreen} />
        <Route path="/docs/:rest*" component={DocsScreen} />

        {/* Fallback — unknown URL goes home */}
        <Route><Redirect to="/" /></Route>
      </Switch>
    </ErrorBoundary>
  );
}

export default function App() {
  const authStatus           = useAppStore((s) => s.authStatus);
  const setAuth              = useAppStore((s) => s.setAuth);
  const setLanguage          = useAppStore((s) => s.setLanguage);
  const repoPath             = useAppStore((s) => s.repoPath);
  const lensUser             = useAppStore((s) => s.lensUser);
  const activeCandidateFolder= useAppStore((s) => s.activeCandidateFolder);
  const setActiveCandidateFolder = useAppStore((s) => s.setActiveCandidateFolder);
  const hasMasterCv          = useAppStore((s) => s.hasMasterCv);
  const setHasMasterCv       = useAppStore((s) => s.setHasMasterCv);
  const setSettings          = useAppStore((s) => s.setSettings);
  const setActiveLlm         = useAppStore((s) => s.setActiveLlm);
  const setTheme             = useAppStore((s) => s.setTheme);

  // 1. Auto-authenticate silent flow on app startup
  useEffect(() => {
    async function init() {
      const prefs = await loadPrefs();
      if (prefs.language) setLanguage(prefs.language, false);
      if (prefs.theme) setTheme(prefs.theme);

      try {
        const session = await silentSignIn();
        if (session) {
          const outcome = await completeSignIn(session, false);
          if (outcome.status === "signed_in") {
            setAuth("signed_in", {
              user: session.user,
              lensUser: outcome.lensUser,
              repoPath: outcome.repoPath,
              accessToken: session.accessToken,
            });
          }
        }
      } catch (err) {
        console.error("Silent sign in failed", err);
      }
    }
    void init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Initialize activeCandidateFolder on sign-in
  useEffect(() => {
    if (authStatus === "signed_in" && lensUser && !activeCandidateFolder) {
      setActiveCandidateFolder(lensUser.folder);
    }
  }, [authStatus, lensUser, activeCandidateFolder, setActiveCandidateFolder]);

  // 3. Sync settings & master CV on candidate/repo changes
  useEffect(() => {
    let active = true;
    async function sync() {
      if (authStatus !== "signed_in" || !repoPath || !activeCandidateFolder) return;
      try {
        const settingsData = await readSettings(repoPath);
        if (active) {
          setSettings(settingsData);
          setActiveLlm(resolveDefaultLlmId(settingsData));
        }
        const exists = await checkMasterCvExists(repoPath, activeCandidateFolder);
        if (active) setHasMasterCv(exists);
      } catch (err) {
        console.error("Failed to sync app data", err);
        if (active) setHasMasterCv(false);
      }
    }
    void sync();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus, repoPath, activeCandidateFolder]);

  if (authStatus === "signed_in") {
    if (hasMasterCv === null) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-canvas dark:bg-[#131825] text-slate-700 dark:text-slate-300">
          <div className="flex flex-col items-center gap-3">
            <span className="h-8 w-8 animate-spin rounded-full border-3 border-slate-300 border-t-slate-800" />
            <p className="text-sm font-medium text-slate-500">Checking credentials…</p>
          </div>
        </div>
      );
    }

    return (
      <Router>
        <SignedInShell />
      </Router>
    );
  }

  return <LoginScreen />;
}
